import {
  clearStoredAccessTokens,
  getAuthParams,
  getSecurityHeaders,
  getStoredAccessToken,
  handleUnauthorizedSession,
  SessionExpiredError,
  setStoredAccessToken,
} from './client';
import api from './client';
import CryptoJS from 'crypto-js';
import { CRYPTO_KEY } from '../config/env';
const LOGIN_PASSWORD_IV = '1020304050607080';

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const isJwtExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 <= Date.now() + 60_000;
};

export const getValidToken = async (forceRefresh = false) => {
  let t = forceRefresh ? null : getStoredAccessToken();
  if (t && isJwtExpired(t)) {
    clearStoredAccessTokens();
    t = null;
  }
  return t;
};

export const hasValidStoredAccessToken = () => {
  const token = getStoredAccessToken();
  if (!token) return false;
  if (isJwtExpired(token)) {
    clearStoredAccessTokens();
    return false;
  }
  return true;
};

const extractToken = (payload) => (
  payload?.access_token ||
  payload?.token ||
  payload?.data?.access_token ||
  payload?.data?.token ||
  ''
);

const extractUser = (payload) => payload?.user || payload?.data?.user || {};

const normalizeUserInfo = (payload, email) => {
  const user = extractUser(payload);
  const client = user?.client || user?.Client || payload?.client || {};
  return {
    clientId: client.id || client.client_id || null,
    clientName: client.name || client.client_name || null,
    clientNaic: client.naic_code || client.naic || null,
    userId: user.id || user.user_id || null,
    userName: user.name || user.username || [user.first_name, user.last_name].filter(Boolean).join(' ') || 'User',
    userEmail: user.email || email,
    role: user.role || user.role_name || 'Client',
  };
};

export const encryptPassword = (password) => {
  if (!CRYPTO_KEY) {
    throw new Error('REACT_APP_CRYPTO_KEY is not configured');
  }

  const payload = CryptoJS.enc.Utf8.parse(password);
  const derivedKey = CryptoJS.enc.Base64.parse(CRYPTO_KEY);
  const iv = CryptoJS.enc.Utf8.parse(LOGIN_PASSWORD_IV);

  return CryptoJS.AES.encrypt(payload, derivedKey, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
    blockSize: 16,
  }).toString();
};

export const login = async ({ email, password }) => {
  const encryptedPassword = encryptPassword(password);
  const response = await api.post('/auth/login', {
    email,
    password: encryptedPassword,
  });
  const payload = response.data || {};
  const token = extractToken(payload);
  if (!token) {
    throw new Error('Login succeeded but no token was returned');
  }

  setStoredAccessToken(token);
  const userInfo = normalizeUserInfo(payload, email);
  localStorage.setItem('userInfo', JSON.stringify(userInfo));
  localStorage.setItem('comply_user', JSON.stringify(userInfo));
  return payload;
};

export const logout = () => {
  clearStoredAccessTokens();
  localStorage.removeItem('userInfo');
  localStorage.removeItem('comply_user');
};

export const fetchWithAnswersAuth = async (url, options = {}) => {
  const send = async (forceRefresh) => {
    const token = await getValidToken(forceRefresh);
    const headers = {
      ...getSecurityHeaders(),
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    return fetch(url, { ...options, headers });
  };

  let response = await send(false);
  if (response.status === 401) {
    response = await send(true);
  }
  if (response.status === 401) {
    handleUnauthorizedSession();
    throw new SessionExpiredError();
  }
  return response;
};

export const getMe = () =>
  api.get('/auth/me', { params: getAuthParams() });
